import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Desc,
  NumberCircle,
  Title,
  TitleBlock,
  WorkflowBgFrame,
  WorkflowCanvas,
  WorkflowIndicator,
  WorkflowIndicatorSegment,
  WorkflowItem,
  WorkflowList,
  WorkflowScroll,
  WorkflowShell,
} from "./styles"

type TWorkflowItem = {
    number: string,
    title: string,
    desc: string,
};

type TWorkflowSection = {
    data: TWorkflowItem[];
};

export const WorkflowSectionCustom: React.FC<TWorkflowSection> = ({ data }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [activeSegment, setActiveSegment] = useState(0);

  const updateScrollState = useCallback(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    const maxScroll = scrollElement.scrollWidth - scrollElement.clientWidth;
    const hasHorizontalScroll = maxScroll > 1;

    setCanScroll(hasHorizontalScroll);
    setActiveSegment(
      hasHorizontalScroll && scrollElement.scrollLeft / maxScroll >= 0.5
        ? 1
        : 0
    );
  }, []);

  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    updateScrollState();

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(updateScrollState);
    const canvasElement = scrollElement.firstElementChild;

    if (resizeObserver) {
      resizeObserver.observe(scrollElement);
    }
    if (resizeObserver && canvasElement) {
      resizeObserver.observe(canvasElement);
    }
    window.addEventListener("resize", updateScrollState);
    scrollElement.addEventListener("scroll", updateScrollState, { passive: true });

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateScrollState);
      scrollElement.removeEventListener("scroll", updateScrollState);
    };
  }, [data, updateScrollState]);

  return (
    <WorkflowShell>
      <WorkflowScroll
        ref={scrollRef}
        aria-label={canScroll ? "Этапы работы" : undefined}
        role={canScroll ? "region" : undefined}
        tabIndex={canScroll ? 0 : undefined}
      >
        <WorkflowCanvas>
          <WorkflowBgFrame />
          <WorkflowList>
            {data.map((item, index) => (
              <WorkflowItem key={index}>
                <NumberCircle>
                  <span>{item.number}</span>
                </NumberCircle>
                <TitleBlock>
                  <Title>{item.title}</Title>
                </TitleBlock>
                <Desc>{item.desc}</Desc>
              </WorkflowItem>
            ))}
          </WorkflowList>
        </WorkflowCanvas>
      </WorkflowScroll>
      {canScroll && (
        <WorkflowIndicator aria-hidden="true">
          <WorkflowIndicatorSegment $isActive={activeSegment === 0} />
          <WorkflowIndicatorSegment $isActive={activeSegment === 1} />
        </WorkflowIndicator>
      )}
    </WorkflowShell>
  )
}
